import { KnownAddressDatalist } from "@/lib/knownAddresses";
import { useEffect, useState, useRef } from "react";
import { useIndustry } from "@/context/IndustryContext";
import { Navigate, useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import QuickCaptureFab from "@/components/QuickCaptureFab";
import { SignOut, House, Users, User as UserIcon, ChartBar, ListChecks, MapTrifold, MapPin, Gear, List, X, Palette, Buildings, ArrowUUpLeft, ClockCounterClockwise, Translate, Repeat, Wrench, MagnifyingGlass, Envelope, ChatCircleText, Broadcast, Database, Warning, ClipboardText, Sparkle, Truck, CreditCard, Hammer, Package, VideoCamera, Snowflake, ShieldCheck, Calendar, CurrencyDollar, Phone, Flame, ChartLineUp, ShieldWarning, Broom, Copy, FileText, PlugsConnected, Drop, Lightning, Key, Kanban, MagicWand, Image as ImageIcon, WhatsappLogo, Camera, EnvelopeSimpleOpen, FileArrowUp, Coins, CheckCircle, Storefront, Scales, LinkSimple, PuzzlePiece, QrCode } from "@phosphor-icons/react";
import { Logo, LogoPill } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearchBar from "@/components/GlobalSearchBar";
import GlobalEscalationBar from "@/components/GlobalEscalationBar";
import AskProManBubble from "@/components/AskProManBubble";
import { TrialBanner, useTenantCtx } from "@/lib/pro";
import { useSimpleMode } from "@/lib/simpleMode";
import { PaywallModal } from "@/components/PaywallModal";
import { useT, setLang as setGlobalLang } from "@/i18n";
import { DisclaimerFooter as LandlordDisclaimerFooter, TosGate as LandlordTosGate } from "@/pages/landlord/ComplianceLayer";

// Simple Mode toggle — one-tap switch for techs. Sits in the mobile header.
function SimpleModeToggle() {
  const { simpleMode, toggleSimpleMode } = useSimpleMode();
  return (
    <button onClick={toggleSimpleMode} type="button" data-testid="simple-mode-toggle"
      title={simpleMode ? "Simple mode ON — advanced options are hidden. Tap to show everything." : "Simple mode OFF — everything visible. Tap to hide advanced options."}
      className={`h-10 px-2 flex items-center gap-1 border-2 border-white/20 font-black uppercase tracking-widest text-[9px] ${simpleMode ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"}`}>
      <span className={`h-4 w-7 relative rounded-full transition ${simpleMode ? "bg-white/30" : "bg-white/20"}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition ${simpleMode ? "left-3.5" : "left-0.5"}`}/>
      </span>
      Simple
    </button>
  );
}
function TierChip({ tenant, compact = false }) {
  if (!tenant) return null;
  const plan = tenant.plan || tenant.plan_tier || "starter";
  const status = tenant.subscription_status || "grandfathered";
  const grandfathered = status === "grandfathered";
  const active = status === "active";
  const inTrial = status === "trial";
  const expired = status === "expired";
  let label = "", cls = "";
  if (tenant.is_demo) { label = "LIVE DEMO"; cls = "bg-[#00C4CC] text-white"; }
  else if (grandfathered) { label = "FOUNDER ACCESS"; cls = "bg-amber-400 text-[#0f172a]"; }
  else if (active) { label = "PRO"; cls = "bg-[#00C4CC] text-white"; }
  else if (inTrial) { label = `TRIAL · ${tenant.subscription_days_left ?? 0}D`; cls = "bg-violet-600 text-white"; }
  else if (expired) { label = "READ-ONLY"; cls = "bg-red-600 text-white"; }
  else if (plan === "pro") { label = "PRO"; cls = "bg-[#00C4CC] text-white"; }
  else if (plan === "trial") { label = "TRIAL"; cls = "bg-violet-600 text-white"; }
  else return null;
  return (
    <span data-testid="tier-chip"
      className={`inline-flex items-center mt-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest whitespace-nowrap ${cls} ${compact ? "" : ""}`}>
      {label}
    </span>
  );
}

// Two-tone chime for new team-chat notifications. Uses WebAudio so no
// asset needs to load. Silently no-ops if AudioContext isn't available
// or the user hasn't yet interacted with the page (browser policy).
let _chatChimeCtx = null;
function playChatChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!_chatChimeCtx) _chatChimeCtx = new Ctx();
    const ctx = _chatChimeCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    [[880, now, 0.08], [1320, now + 0.08, 0.12]].forEach(([freq, start, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + dur + 0.02);
    });
  } catch { /* older browsers — silent */ }
}

function LangToggle({ dark = true }) {
  const { lang } = useT();
  const change = async (next) => {
    if (next === lang) return;
    setGlobalLang(next);
    try { await api.patch("/auth/language", { language: next }); } catch {}
    // Force refresh so backend-localized job data reloads
    window.location.reload();
  };
  const base = dark
    ? "border border-slate-600 text-white"
    : "border border-[#0f172a] text-[#0f172a]";
  const activeCls = dark ? "bg-[#00C4CC] border-[#00C4CC]" : "bg-[#0f172a] text-white";
  return (
    <div className={`inline-flex ${base}`} data-testid="lang-toggle">
      <button onClick={() => change("en")} data-testid="lang-en"
        className={`px-2 h-8 text-[11px] font-black uppercase tracking-widest ${lang === "en" ? activeCls : ""}`}>EN</button>
      <button onClick={() => change("es")} data-testid="lang-es"
        className={`px-2 h-8 text-[11px] font-black uppercase tracking-widest ${lang === "es" ? activeCls : ""}`}>ES</button>
    </div>
  );
}

export function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-display font-black text-2xl">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Super-admin can view admin routes only via impersonation
  if (user.platform_role === "super_admin" && !localStorage.getItem("impersonate_tenant")) {
    return <Navigate to="/super-admin" replace />;
  }
  if (role && user.role !== role && user.platform_role !== "super_admin") {
    return <Navigate to={user.role === "admin" ? "/admin" : "/worker"} replace />;
  }
  return (
    <>
      <DemoBanner user={user}/>
      {children}
    </>
  );
}

// Sticky-top banner shown to every demo-tenant visitor. Purposely rendered
// OUTSIDE the shells (AdminShell / WorkerShell) so vertical pages that build
// their own layout (RouteRadar, HomeHealthClients, HvacUnits, RoofingJobs)
// still surface the demo state.
function DemoBanner({ user }) {
  if (!user?.is_demo) return null;
  return (
    <div
      data-testid="demo-mode-banner"
      className="sticky top-0 z-[60] w-full bg-gradient-to-r from-[#00C4CC] to-cyan-500 text-white px-4 py-2 flex items-center justify-between gap-3 shadow"
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center shrink-0 text-[10px]">▶</div>
        <span className="text-xs font-bold tracking-wide truncate">
          Live demo — every feature works. Playground resets every 30 min. Sign up to keep yours.
        </span>
      </div>
      <Link
        to="/signup"
        data-testid="demo-banner-signup"
        className="h-7 px-3 rounded-md bg-white text-[#0f172a] hover:bg-slate-100 font-black text-[10px] uppercase tracking-widest transition-colors shrink-0 inline-flex items-center"
      >
        Claim Your Workspace →
      </Link>
    </div>
  );
}

export function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-display font-black text-2xl">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.platform_role !== "super_admin") {
    return <Navigate to={user.role === "admin" ? "/admin" : "/worker"} replace />;
  }
  // Clear any lingering impersonation while viewing super-admin console
  if (localStorage.getItem("impersonate_tenant")) {
    localStorage.removeItem("impersonate_tenant");
    localStorage.removeItem("impersonate_tenant_name");
  }
  return children;
}

function ImpersonationBanner() {
  const nav = useNavigate();
  const [name, setName] = useState(localStorage.getItem("impersonate_tenant_name"));
  useEffect(() => {
    const check = () => setName(localStorage.getItem("impersonate_tenant_name"));
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, []);
  if (!name) return null;
  const exit = () => {
    localStorage.removeItem("impersonate_tenant");
    localStorage.removeItem("impersonate_tenant_name");
    nav("/super-admin");
  };
  return (
    <div className="bg-[#00C4CC] text-white px-4 py-2 flex items-center justify-between text-xs sm:text-sm font-bold uppercase tracking-wider" data-testid="impersonation-banner">
      <span>Viewing as: <b>{name}</b></span>
      <button onClick={exit} data-testid="impersonation-exit-btn"
        className="inline-flex items-center gap-1 px-3 py-1 bg-white text-[#0f172a]">
        <ArrowUUpLeft weight="bold" size={12}/> Exit
      </button>
    </div>
  );
}

function useTenant() {
  const { tenant } = useTenantCtx();
  return tenant;
}

/**
 * DesignVersionPill — top-bar toggle so users are never trapped in one version.
 * Reads localStorage `fr_admin_dashboard_version` ("v2" | "classic" | null → V2 default).
 * • On classic: shows a "✨ Try New Design" pill that flips to V2 and reloads.
 * • On V2: shows a subtle "V1" pill for going back.
 * Reload is intentional — App.js re-evaluates the router switch on mount.
 */
function DesignVersionPill() {
  const [pref, setPref] = useState(() => {
    try { return localStorage.getItem("fr_admin_dashboard_version") || "v2"; } catch { return "v2"; }
  });
  const flip = (to) => {
    try { localStorage.setItem("fr_admin_dashboard_version", to); } catch {}
    setPref(to);
    // Reload so the router picks up the new preference on this route.
    window.location.reload();
  };
  if (pref === "classic") {
    return (
      <button onClick={() => flip("v2")}
        data-testid="design-version-pill-try-v2"
        title="Switch to the new design (V2)"
        className="ml-auto shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-gradient-to-r from-[#FF6A2B] to-[#B12FBB] text-white text-[10px] font-black uppercase tracking-widest shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">
        <Sparkle weight="fill" size={12}/>
        Try New Design
      </button>
    );
  }
  return (
    <button onClick={() => flip("classic")}
      data-testid="design-version-pill-classic"
      title="Switch back to the classic design"
      className="ml-auto shrink-0 inline-flex items-center h-8 px-3 rounded-full bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:text-slate-800 transition-colors">
      Classic View
    </button>
  );
}


function NavGroup({ label, storageKey, defaultOpen = false, count, href, children }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(storageKey); return v === null ? defaultOpen : v === "1"; }
    catch { return defaultOpen; }
  });
  const toggle = (e) => {
    e.stopPropagation();
    setOpen(v => {
      const nv = !v;
      try { localStorage.setItem(storageKey, nv ? "1" : "0"); } catch {}
      return nv;
    });
  };
  const onHeaderClick = () => {
    if (href) nav(href);
    else setOpen(v => {
      const nv = !v;
      try { localStorage.setItem(storageKey, nv ? "1" : "0"); } catch {}
      return nv;
    });
  };
  return (
    <div>
      <div className="w-[calc(100%-0.5rem)] mx-2 mt-2 mb-0.5 flex items-center gap-2 rounded-md transition-colors hover:bg-slate-800/60 group">
        <button onClick={onHeaderClick} data-testid={`nav-group-${storageKey}`}
          className="flex-1 min-w-0 px-2 py-1.5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-white/40 group-hover:text-white/80 text-left">
          <span>{label}</span>
          {typeof count === "number" && count > 0 && (
            <span className="ml-auto text-[9px] font-black text-white/40 group-hover:text-white/70">{count}</span>
          )}
        </button>
        <button onClick={toggle} data-testid={`nav-group-toggle-${storageKey}`}
          aria-label={open ? "Collapse" : "Expand"}
          className="pr-3 py-1.5 text-[13px] leading-none text-white/40 group-hover:text-white/80 shrink-0 tabular-nums">
          <span>{open ? "−" : "+"}</span>
        </button>
      </div>
      {open && <div className="space-y-0.5">{children}</div>}
    </div>
  );
}

export function AdminShell({ children }) {
  const [killswitch, setKillswitch] = useState(null);
  const [majorPortfolios, setMajorPortfolios] = useState([]);
  const industry = useIndustry();
  const isPM = industry.industry === "pm";
  const isHVAC = industry.industry === "hvac";
  const isRoofing = industry.industry === "roofing";
  const isPest = industry.industry === "pest_control";
  const isHomeHealth = industry.industry === "home_health";
  const isLandlord = industry.industry === "landlord";
  const isLandscaping = industry.industry === "landscaping";
  const isPlumbing = industry.industry === "plumbing";
  const isElectrical = industry.industry === "electrical";
  const isHandyman = industry.industry === "handyman";
  const isVendors = industry.industry === "vendors";
  // Narrow verticals — no PM/vendor sidebar clutter (Radars, InspectRadar,
  // Truck & Inventory, Portfolio, Docs & Sync). These verticals get their
  // own dedicated group + the universal Daily Tools + Team.
  const isNarrowVertical = isLandlord || isLandscaping || isHomeHealth
    || isPlumbing || isElectrical || isHandyman || isVendors;
  useEffect(() => {
    const load = () => api.get("/system/email-killswitch").then(r => setKillswitch(r.data)).catch(() => setKillswitch(null));
    load();
    // Load major portfolios once for the sidebar group
    api.get("/major-portfolios").then(r => setMajorPortfolios(r.data.portfolios || [])).catch(() => setMajorPortfolios([]));
    const handler = () => load();
    window.addEventListener("killswitch-changed", handler);
    return () => window.removeEventListener("killswitch-changed", handler);
  }, []);

  // ── Auto-heal once per browser session ──
  // Runs the WO-number/unit sweep silently for this tenant on first
  // admin page load per session. Backend guards double-runs with a
  // tenant flag + honors the "disabled" preference, so this is safe
  // even if we hit it multiple times.
  useEffect(() => {
    const KEY = "wo_unit_auto_heal_checked";
    if (sessionStorage.getItem(KEY) === "1") return;
    sessionStorage.setItem(KEY, "1");
    api.post("/admin/heal-wo-unit-labels/auto-run-once").then(({ data }) => {
      if (!data?.ran || !data?.healed) return;
      // Show a compact toast with a "Never again" action
      const disable = async () => {
        try {
          await api.post("/admin/heal-wo-unit-labels/set-auto", { enabled: false });
          toast.success("Auto-heal disabled — flip back on in Portfolio Cleanup");
        } catch { toast.error("Couldn't save preference"); }
      };
      toast.success(
        `Auto-fixed ${data.healed} phantom WO unit${data.healed === 1 ? "" : "s"}`,
        {
          description: "You can re-run or turn this off in Portfolio Cleanup.",
          duration: 10000,
          action: { label: "Never again", onClick: disable },
        }
      );
    }).catch(() => { /* silent */ });
  }, []);
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const tenant = useTenant();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Global ⌘K / Ctrl+K → focus the top search bar
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        document.querySelector('[data-testid="global-search-input"]')?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
  const logoUrl = tenant?.logo_path ? `${API}/tenant/logo/${tenant.id}` : null;
  useEffect(() => {
    if (!tenant?.id) return;
    try { localStorage.setItem("fr_last_tenant", JSON.stringify({ id: tenant.id, name: tenant.name, logo: !!tenant.logo_path })); } catch {}
  }, [tenant?.id, tenant?.name, tenant?.logo_path]);
  const brandColor = tenant?.brand_color || "#0f172a";
  // Staff (Grace/Mykee) cannot see Reports/Branding/Settings/Delete
  const isFullAdmin = user && (user.platform_role === "super_admin" || (user.role === "admin" && (!user.office_role || user.office_role === "manager")));
  const isFounder = user && (user.platform_role === "super_admin" || user.is_founder);
  const hasEnterprise = user && (user.platform_role === "super_admin" || user.enterprise_access);
  // ── Sidebar mode (Maintenance vs Full Operations)
  //    Auto-defaults from tenant.plan / tenant.modules but user can override
  //    via the toggle. Preference is stored in localStorage.
  const planFromTenant = (() => {
    const modules = tenant?.modules || [];
    if (tenant?.plan === "maintenance" || (modules.length === 1 && modules[0] === "maintenance")) return "maintenance";
    return "full";
  })();
  const [sidebarMode, setSidebarMode] = useState(() => {
    return localStorage.getItem("sidebar_mode") || planFromTenant;
  });
  useEffect(() => { localStorage.setItem("sidebar_mode", sidebarMode); }, [sidebarMode]);
  const [liveFeedUnread, setLiveFeedUnread] = useState(0);
  useEffect(() => {
    if (!user || !["admin", "founder"].includes(user.role)) return;
    let alive = true;
    const tick = () => {
      api.get("/kraaft/live-feed/unread-count")
        .then(r => { if (alive) setLiveFeedUnread(r.data?.total || 0); })
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 15000); // gentle poll — 15s
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, loc.pathname]);

  // Team-chat unread — powers the badge on the pinned Team Chat nav item so
  // office sees "3 new from Marcus" without opening the app. Also fires an
  // in-app toast + audible chime when the count RISES and the admin isn't
  // currently on the chat page — this is the "no notification" fix techs
  // asked for (Jhan/Ciarra: "no notifications and pics take 5 min").
  const [teamChatUnread, setTeamChatUnread] = useState(0);
  const teamChatPrevRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = () => {
      api.get("/team-chat/unread-total")
        .then(r => {
          if (!alive) return;
          const next = r.data?.total || 0;
          const prev = teamChatPrevRef.current;
          setTeamChatUnread(next);
          // Home-screen icon badge (like Gmail/TikTok/X). Only works when the
          // app is installed as a PWA: Android/desktop Chrome via "Install app",
          // iPhone via Share → Add to Home Screen (iOS 16.4+). Regular Safari
          // tabs get no badge — the call silently no-ops. Also honors the
          // user's notification permission.
          try {
            if (typeof navigator !== "undefined" && "setAppBadge" in navigator) {
              if (next > 0) navigator.setAppBadge(next).catch(() => {});
              else navigator.clearAppBadge?.().catch(() => {});
            }
          } catch { /* silent — feature unsupported on this browser */ }
          const onChatPage = loc.pathname.startsWith("/admin/team-chat") || loc.pathname.startsWith("/worker/team-chat");
          if (prev != null && next > prev && !onChatPage) {
            const delta = next - prev;
            toast.info(`💬 ${delta} new team chat message${delta === 1 ? "" : "s"}`, {
              action: { label: "Open", onClick: () => nav("/admin/team-chat") },
              duration: 6000,
            });
            playChatChime();
          }
          teamChatPrevRef.current = next;
        })
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loc.pathname]);

  const navItem = (to, icon, label) => {
    const active = loc.pathname === to || (to !== "/admin" && loc.pathname.startsWith(to + "/")) || (to === "/admin" && loc.pathname === "/admin");
    const isLiveFeed = to === "/admin/live-feed";
    const isTeamChat = to === "/admin/team-chat";
    const showLiveBadge = isLiveFeed && liveFeedUnread > 0 && !active;
    const showChatBadge = isTeamChat && teamChatUnread > 0 && !active;
    const badgeCount = showLiveBadge ? liveFeedUnread : showChatBadge ? teamChatUnread : 0;
    const showBadge = showLiveBadge || showChatBadge;
    return (
      <Link key={`${to}::${label}`} to={to} data-testid={`nav-${label.toLowerCase()}`}
        onClick={() => setDrawerOpen(false)}
        className={`relative flex items-center gap-3 mx-2 px-3 py-2.5 rounded-xl font-bold uppercase text-[15px] tracking-wide transition-colors ${
          active
            ? "bg-gradient-to-r from-[#FF6A2B]/20 to-[#B12FBB]/10 text-white"
            : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
        }`}>
        {active && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-[#FF6A2B] to-[#B12FBB]"/>
        )}
        <span className="relative">
          {icon}
          {showBadge && (
            <span data-testid={isTeamChat ? "team-chat-badge" : "live-feed-badge"}
                  className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center border border-slate-900 animate-pulse">
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
          )}
        </span>
        {label}
      </Link>
    );
  };
  const SidebarContent = (
    <>
      <div className="px-5 pt-5 pb-4 flex items-center justify-between gap-3" style={{ background: brandColor === "#0f172a" ? "" : `${brandColor}22` }}>
        <div className="flex items-center gap-2 min-w-0" data-testid="tenant-name-header" title={tenant?.name || "FieldRadar"}>
          <LogoPill height={30}/>
          <span className="px-2 py-0.5 rounded-md bg-[#14B8A6] text-white text-[10px] font-black uppercase tracking-widest" data-testid="sidebar-pro-badge">Pro</span>
        </div>
        <button onClick={() => setDrawerOpen(false)} data-testid="admin-drawer-close"
          className="md:hidden text-white p-1 -mr-1">
          <X weight="bold" size={22}/>
        </button>
      </div>
      <div className="mx-4 h-px bg-slate-800/60"/>
      <nav className="flex-1 py-2 space-y-0.5 overflow-y-auto">
        {/* Sidebar mode toggle — Maintenance vs Full Operations */}
        <div className="mx-3 mt-1 mb-2 grid grid-cols-2 gap-1 p-1 bg-slate-800 border border-slate-700 rounded-lg"
          data-testid="sidebar-mode-toggle">
          <button onClick={() => setSidebarMode("maintenance")}
            data-testid="sidebar-mode-maintenance"
            className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest rounded transition-colors ${sidebarMode === "maintenance" ? "bg-cyan-500 text-slate-900" : "text-slate-400 hover:text-white"}`}>
            🔧 Maintenance
          </button>
          <button onClick={() => setSidebarMode("full")}
            data-testid="sidebar-mode-full"
            className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest rounded transition-colors ${sidebarMode === "full" ? "bg-cyan-500 text-slate-900" : "text-slate-400 hover:text-white"}`}>
            ⚙️ Full Ops
          </button>
        </div>
        {/* ⌘K palette teaser — clicking focuses the top search bar */}
        <button onClick={() => document.querySelector('[data-testid="global-search-input"]')?.focus()}
          data-testid="cmdk-teaser"
          className="w-[calc(100%-1.5rem)] mx-3 mt-1 mb-2 px-3 py-2 border border-slate-700 hover:border-cyan-500 rounded-lg flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
          <MagnifyingGlass weight="bold" size={13}/>
          <span className="text-[11px] font-medium">Jump to anything…</span>
          <span className="ml-auto text-[9px] font-mono bg-slate-700 px-1.5 py-0.5 rounded">⌘K</span>
        </button>

        {sidebarMode === "maintenance" ? (
          <>
            {/* MAINTENANCE MODE — same 5 core always-visible, everything else in collapsed groups. */}
            {navItem("/admin", <House weight="bold" size={20}/>, "Dashboard")}
            {navItem("/admin/live", <MapPin weight="fill" size={20} className="text-cyan-500"/>, "Live Field")}
            {navItem("/admin/team-chat", <ChatCircleText weight="fill" size={20} className="text-teal-400"/>, "Team Chat")}
            {navItem("/admin/jobs", <ListChecks weight="bold" size={20}/>, `${industry.labels?.work_order || "Work Order"}s`)}
            {navItem("/admin/jobs-board", <Kanban weight="fill" size={20} className="text-violet-500"/>, "Jobs Board")}
            {navItem("/admin/parts-queue", <Wrench weight="fill" size={20} className="text-orange-500"/>, "Parts Queue")}
            {isPM && navItem("/admin/lineup", <Broadcast weight="fill" size={20} className="text-cyan-500"/>, "SweeperRadar™")}
            {isPM && navItem("/admin/schedule", <Calendar weight="fill" size={20} className="text-cyan-600"/>, "Schedule · Week")}
            {isPM && navItem("/admin/schedule/month", <Calendar weight="regular" size={20} className="text-cyan-500 opacity-75"/>, "Schedule · Month")}
            {isPM && navItem("/admin/turns", <Repeat weight="bold" size={20}/>, "Turns")}
            {isPM && navItem("/admin/inspections", <Broadcast weight="fill" size={20} style={{ color: brandColor === "#0f172a" ? undefined : brandColor }}/>, "InspectRadar™")}
            <NavGroup label="Quick Tools" storageKey="nav-g-m-quick" defaultOpen={false} count={isPM ? 7 : 2}>
              {navItem("/admin/wo-paste", <MagicWand weight="fill" size={20} className="text-indigo-500"/>, "Text → WOs")}
              {navItem("/admin/status-reply", <EnvelopeSimpleOpen weight="fill" size={20} className="text-teal-500"/>, "Status Reply")}
              {isPM && navItem("/admin/live-feed", <Broadcast weight="fill" size={20} className="text-red-500"/>, "Live Feed")}
              {isPM && navItem("/admin/appliance-iq", <Wrench weight="fill" size={20} className="text-amber-600"/>, "ApplianceIQ")}
              {isPM && navItem("/admin/turns-board", <Broadcast weight="fill" size={20} className="text-cyan-500"/>, "Turns Board · Live")}
              {isPM && navItem("/admin/vacant-security", <ShieldCheck weight="fill" size={20} className="text-emerald-500"/>, "Vacant Security · Alarms")}
              {isPM && navItem("/admin/purge/truck-sheet", <Truck weight="fill" size={20} className="text-amber-500"/>, "Truck Day · Purge")}
              {isPM && navItem("/admin/property-inspections", <ClipboardText weight="fill" size={20} className="text-violet-500"/>, "Property Inspections")}
            </NavGroup>

            {majorPortfolios.length > 0 && isPM && (
              <NavGroup label="Major Portfolios" storageKey="nav-g-m-major" href="/admin/major-portfolios-hub" defaultOpen={false} count={majorPortfolios.length}>
                {majorPortfolios.map(p => navItem(`/admin/portfolios/${p.slug}`,
                  <Buildings weight="fill" size={20} style={{ color: p.brand_color }}/>,
                  p.name))}
              </NavGroup>
            )}

            {!isNarrowVertical && <NavGroup label="Radars" storageKey="nav-g-m-radars" href="/admin/radars" defaultOpen={false}>
              {isPM && navItem("/admin/walk-radar", <Broadcast weight="fill" size={20} className="opacity-70"/>, "WalkRadar™")}
              {isPM && navItem("/admin/tenant-inspections", <Broadcast weight="fill" size={20} style={{ color: brandColor === "#0f172a" ? undefined : brandColor }}/>, "TenantRadar™")}
              {isPM && navItem("/admin/lineup", <Broadcast weight="fill" size={20} style={{ color: brandColor === "#0f172a" ? undefined : brandColor }}/>, "SweeperRadar™")}
            {isPM && navItem("/admin/schedule", <Calendar weight="fill" size={20} className="text-cyan-600"/>, "Schedule · Week")}
            {isPM && navItem("/admin/schedule/month", <Calendar weight="regular" size={20} className="text-cyan-500 opacity-75"/>, "Schedule · Month")}
              {isPM && navItem("/admin/parking-lot", <Broadcast weight="fill" size={20} style={{ color: brandColor === "#0f172a" ? undefined : brandColor }}/>, "ClosureRadar™")}
              {(hasEnterprise || isFullAdmin) && navItem("/admin/projects", <Hammer weight="fill" size={20} className="text-fuchsia-500"/>, "Project Radar™")}
              {navItem("/admin/reviews", <Broadcast weight="fill" size={20} style={{ color: brandColor === "#0f172a" ? undefined : brandColor }}/>, "Reviews")}
              {navItem("/admin/booking", <Calendar weight="fill" size={20} className="text-emerald-500"/>, "Booking Radar")}
              {navItem("/admin/pitch", <ChartLineUp weight="fill" size={20} className="text-fuchsia-500"/>, "Pitch Radar")}
              {isPM && navItem("/admin/leasing", <Users weight="fill" size={20} className="text-cyan-500"/>, "Leasing Agents")}
              {navItem("/admin/integrations", <PlugsConnected weight="fill" size={20} className="text-fuchsia-500"/>, "Integrations")}
              {isPM && navItem("/admin/bridge-inbox", <PlugsConnected weight="fill" size={20} className="text-fuchsia-500"/>, "Bridge Inbox")}
            </NavGroup>}

            {isPM && <NavGroup label="Chew Flats (LaSalle)" storageKey="nav-g-m-chew" defaultOpen={false} count={4}>
              {navItem("/admin/tenant-bridge", <ChatCircleText weight="fill" size={20} className="text-cyan-400"/>, "TenantBridge · Inbox")}
              {navItem("/admin/chew-hub", <Key weight="fill" size={20} className="text-amber-500"/>, "Student Portal Hub")}
              {navItem("/admin/chew-tickets", <ChatCircleText weight="fill" size={20} className="text-cyan-500"/>, "Chew Flats Inbox")}
              {navItem("/admin/chew-punchlist", <ClipboardText weight="fill" size={20} className="text-cyan-500"/>, "Walk Punchlist")}
            </NavGroup>}

            {isHVAC && <NavGroup label="❄️ HVAC Tools" storageKey="nav-g-m-hvac" defaultOpen={false} count={4}>
              {navItem("/admin/hvac", <Snowflake weight="fill" size={20} className="text-cyan-400"/>, "HVAC Dashboard")}
              {navItem("/admin/hvac/units", <Snowflake weight="fill" size={20} className="text-cyan-400"/>, "Equipment Radar · PMs + AI")}
              {navItem("/admin/hvac/refrigerant-log", <Snowflake weight="fill" size={20} className="text-cyan-400"/>, "Refrigerant Log · EPA 608")}
              {navItem("/admin/parts-reorder", <Snowflake weight="fill" size={20} className="text-fuchsia-400"/>, "Truck Reorder · Parts")}
            </NavGroup>}

            {isRoofing && <NavGroup label="🌧️ Roofing Tools" storageKey="nav-g-m-roof" defaultOpen={false} count={2}>
              {navItem("/admin/roofing/jobs", <House weight="fill" size={20} className="text-amber-400"/>, "Job Radar · Pipeline + AI")}
              {navItem("/admin/roofing/weather-dispatch", <Broadcast weight="fill" size={20} className="text-cyan-400"/>, "Weather Dispatch · 48h")}
            </NavGroup>}

            {isPest && <NavGroup label="🐛 Pest Control" storageKey="nav-g-m-pest" defaultOpen={false} count={3}>
              {navItem("/admin/pest", <Broadcast weight="fill" size={20} className="text-emerald-400"/>, "Pest Dashboard")}
              {navItem("/admin/pest/pesticide-log", <Broadcast weight="fill" size={20} className="text-emerald-400"/>, "Pesticide Log · State Compliance")}
              {navItem("/admin/pest/route-radar", <Broadcast weight="fill" size={20} className="text-cyan-400"/>, "Route Radar · Recurring")}
            </NavGroup>}

            {isHomeHealth && <NavGroup label="❤️ Home Health" storageKey="nav-g-m-hh" defaultOpen={false} count={2}>
              {navItem("/admin/home-health", <House weight="fill" size={20} className="text-fuchsia-400"/>, "Home Health Dashboard")}
              {navItem("/admin/home-health/clients", <House weight="fill" size={20} className="text-fuchsia-400"/>, "Client Care Radar · AI + PDF")}
            </NavGroup>}

            {isLandlord && <NavGroup label="🏠 Landlord Autopilot" storageKey="nav-g-m-landlord" defaultOpen={false} count={3}>
              {navItem("/admin/landlord/rentals", <House weight="fill" size={20} className="text-sky-400"/>, "Rentals · Magic Portal")}
              {navItem("/admin/landlord/repairs", <Broadcast weight="fill" size={20} className="text-fuchsia-400"/>, "Repair Inbox · AI Triage")}
              {navItem("/admin/landlord/vendors", <Package weight="fill" size={20} className="text-emerald-400"/>, "Vendor Book")}
            </NavGroup>}

            {isLandscaping && <NavGroup label="🌲 Landscaping & Snow" storageKey="nav-g-m-landscape" defaultOpen={false} count={1}>
              {navItem("/admin/landscaping", <House weight="fill" size={20} className="text-emerald-400"/>, "Ops Center · Route + Snow")}
            </NavGroup>}

            {isPlumbing && <NavGroup label="💧 Plumbing" storageKey="nav-g-m-plumb" defaultOpen={false} count={5}>
              {navItem("/admin/plumbing", <Drop weight="fill" size={20} className="text-blue-400"/>, "Plumbing Dashboard")}
              {navItem("/admin/plumbing/leak-diagnose", <Sparkle weight="fill" size={20} className="text-blue-400"/>, "AI Leak Diagnose")}
              {navItem("/admin/jobs", <Broadcast weight="fill" size={20} className="text-blue-400"/>, "Service Calls")}
              {navItem("/admin/parts-reorder", <Package weight="fill" size={20} className="text-blue-400"/>, "Truck Parts Reorder")}
              {navItem("/admin/booking", <Broadcast weight="fill" size={20} className="text-emerald-400"/>, "Booking Radar")}
            </NavGroup>}

            {isElectrical && <NavGroup label="⚡ Electrical" storageKey="nav-g-m-elec" defaultOpen={false} count={5}>
              {navItem("/admin/electrical", <Lightning weight="fill" size={20} className="text-yellow-400"/>, "Electrical Dashboard")}
              {navItem("/admin/electrical/load-calc", <Sparkle weight="fill" size={20} className="text-yellow-400"/>, "AI Load-Calc")}
              {navItem("/admin/jobs", <Broadcast weight="fill" size={20} className="text-yellow-400"/>, "Service Calls")}
              {navItem("/admin/inspections", <Broadcast weight="fill" size={20} className="text-yellow-400"/>, "InspectRadar™ · NEC")}
              {navItem("/admin/booking", <Broadcast weight="fill" size={20} className="text-emerald-400"/>, "Booking Radar")}
            </NavGroup>}

            {isHandyman && <NavGroup label="🔧 Handyman" storageKey="nav-g-m-handy" defaultOpen={false} count={4}>
              {navItem("/admin/handyman", <Hammer weight="fill" size={20} className="text-purple-400"/>, "Handyman Dashboard")}
              {navItem("/admin/booking", <Broadcast weight="fill" size={20} className="text-purple-400"/>, "Booking Radar · Self-Book")}
              {navItem("/admin/jobs", <Broadcast weight="fill" size={20} className="text-purple-400"/>, "Jobs · Tap-to-Pay")}
              {navItem("/admin/drafts", <Sparkle weight="fill" size={20} className="text-purple-400"/>, "AI Drafts Queue")}
            </NavGroup>}

            {isVendors && <NavGroup label="🔌 Maintenance Vendor" storageKey="nav-g-m-vend" defaultOpen={false} count={4}>
              {navItem("/admin/vendors", <PlugsConnected weight="fill" size={20} className="text-teal-400"/>, "Vendor Dashboard")}
              {navItem("/admin/bridge-inbox", <PlugsConnected weight="fill" size={20} className="text-teal-400"/>, "Bridge Inbox · Every PM")}
              {navItem("/admin/jobs", <Broadcast weight="fill" size={20} className="text-teal-400"/>, "Vendor Work Orders")}
              {navItem("/admin/drafts", <Sparkle weight="fill" size={20} className="text-teal-400"/>, "AI Bid Drafts")}
            </NavGroup>}

            <NavGroup label="Daily Tools" storageKey="nav-g-m-daily" href="/admin/daily-tools" defaultOpen={false} count={4}>
              {navItem("/admin/inbox", <Envelope weight="bold" size={20}/>, "Inbox")}
              {navItem("/admin/calendar", <Calendar weight="fill" size={20} className="text-cyan-500"/>, "Team Calendar")}
              {navItem("/admin/voice-reception", <Phone weight="fill" size={20} className="text-fuchsia-500"/>, "Voice Reception")}
              {navItem("/admin/emergency-map?address=1200%20Sample%20St", <MapTrifold weight="bold" size={20}/>, "Map")}
            </NavGroup>

            {!isNarrowVertical && <NavGroup label="Truck & Inventory" storageKey="nav-g-m-truck" href="/admin/truck-inventory" defaultOpen={false} count={4}>
              {navItem("/truck-stock", <Package weight="fill" size={20} className="text-amber-500"/>, "My Truck Stock")}
              {navItem("/admin/truck-stock", <Truck weight="fill" size={20} className="text-amber-500"/>, "Truck Roster")}
              {isHVAC && navItem("/admin/filter-master-list", <Snowflake weight="fill" size={20} className="text-cyan-500"/>, "Filter List")}
              {navItem("/admin/duplicates", <Copy weight="fill" size={20} className="text-fuchsia-500"/>, "Dupes Queue")}
              {navItem("/admin/portfolio-merge", <Copy weight="fill" size={20} className="text-red-500"/>, "Merge Buildings")}
              {navItem("/admin/jobs-dedupe", <Copy weight="fill" size={20} className="text-orange-500"/>, "Merge WOs")}
              {navItem("/admin/ready-to-bill", <CurrencyDollar weight="fill" size={20} className="text-emerald-500"/>, "Ready to Bill")}
              {navItem("/admin/receipts", <Warning weight="fill" size={20} className="text-orange-500"/>, "Receipt Purgatory")}
              {navItem("/admin/maintainx", <Wrench weight="fill" size={20} className="text-cyan-500"/>, "MaintainX")}
              {navItem("/admin/quick-report", <Sparkle weight="fill" size={20} className="text-purple-500"/>, "Quick Report")}
            </NavGroup>}

            {!isNarrowVertical && <NavGroup label="Documents & Sync" storageKey="nav-g-m-docs" href="/admin/documents" defaultOpen={false} count={3}>
              {navItem("/admin/esign", <FileText weight="fill" size={20} className="text-indigo-500"/>, "E-Sign")}
              {navItem("/admin/import-wo", <Copy weight="fill" size={20} className="text-cyan-500"/>, "Import WO")}
              {navItem("/admin/imports/appfolio", <FileArrowUp weight="fill" size={20} className="text-amber-500"/>, "AppFolio CSV Import")}
              {navItem("/admin/imports/grok", <LinkSimple weight="fill" size={20} className="text-fuchsia-500"/>, "Grok Inspection Importer")}
              {navItem("/admin/settings/inspection-templates", <ListChecks weight="fill" size={20} className="text-fuchsia-500"/>, "InspectRadar Templates")}
              {navItem("/admin/office-inbox", <Sparkle weight="fill" size={20} className="text-indigo-500"/>, "Office Inbox")}
              {isPM && navItem("/admin/move-radar", <Repeat weight="fill" size={20} className="text-indigo-500"/>, "Move Radar")}
              {navItem("/admin/clickup", <ClipboardText weight="fill" size={20} className="text-purple-500"/>, "ClickUp Bridge")}
              {navItem("/admin/whatsapp-import", <WhatsappLogo weight="fill" size={20} className="text-emerald-500"/>, "WhatsApp Receipts")}
              {navItem("/admin/kraaft-import", <Package weight="fill" size={20} className="text-fuchsia-500"/>, "Kraaft Bulk Import")}
              {navItem("/admin/bulk-photos", <Camera weight="fill" size={20} className="text-cyan-500"/>, "Bulk Photos → WO")}
              {navItem("/admin/property-inspections", <ClipboardText weight="fill" size={20} className="text-violet-500"/>, "Property Inspections")}
              {isPM && navItem("/admin/seasonal", <Snowflake weight="fill" size={20} className="text-cyan-500"/>, "Snow & Lawn")}
            </NavGroup>}

            {!isNarrowVertical && <NavGroup label={isPM ? "Portfolio" : "Book of Business"} storageKey="nav-g-m-portfolio" href="/admin/portfolio-hub" defaultOpen={false} count={1}>
              {navItem("/admin/assets", <Package weight="fill" size={20} className="text-fuchsia-500"/>, "Assets")}
            </NavGroup>}

            <NavGroup label="Team" storageKey="nav-g-m-team" href="/admin/team-hub" defaultOpen={false} count={1}>
              {navItem("/admin/workers", <Users weight="bold" size={20}/>, "Team")}
              {navItem("/admin/clients", <UserIcon weight="fill" size={20} className="text-emerald-500"/>, "Client Pages")}
            </NavGroup>

            {isFullAdmin && (
              <NavGroup label="Admin" storageKey="nav-g-m-admin" href="/admin/admin-hub" defaultOpen={false} count={2}>
                {navItem("/admin/settings", <Gear weight="bold" size={20}/>, "Settings")}
                {navItem("/admin/terminology", <Translate weight="bold" size={20} className="text-cyan-400"/>, "Terminology")}
              </NavGroup>
            )}
          </>
        ) : (
          <>
        {/* ═══════════════════════════════════════════════════════════════
            FULL OPS MODE — Clean 7-bucket structure
            Core · Inbox & Comms · Radars · Property & Portfolio ·
            Bookkeeper · AI Tools · Integrations & Sync · Cleanup
            (+ industry-specific groups preserved where they appear)
        ═══════════════════════════════════════════════════════════════ */}

        {/* WORKSPACE */}
        <NavGroup label="Workspace" storageKey="nav-g-workspace" defaultOpen={true}>
          {navItem("/admin", <House weight="bold" size={20}/>, "Dashboard")}
          {navItem("/admin/inbox", <EnvelopeSimpleOpen weight="fill" size={20} className="text-amber-500"/>, "Inbox")}
          {navItem("/admin/team-chat", <ChatCircleText weight="fill" size={20} className="text-teal-400"/>, "Team Chat")}
          {navItem("/admin/live-feed", <Broadcast weight="fill" size={20} className="text-red-500"/>, "Live Feed")}
          {navItem("/admin/live", <MapPin weight="fill" size={20} className="text-cyan-500"/>, "Live Field")}
        </NavGroup>

        {/* OPERATIONS */}
        <NavGroup label="Operations" storageKey="nav-g-operations" defaultOpen={true}>
          {navItem("/admin/jobs", <ListChecks weight="bold" size={20} className="text-slate-200"/>, `${industry.labels?.work_order || "Work Order"}s`)}
          {navItem("/admin/jobs-board", <Kanban weight="fill" size={20} className="text-violet-500"/>, "Jobs Board")}
          {navItem("/admin/parts-queue", <Wrench weight="fill" size={20} className="text-orange-500"/>, "Parts Queue")}
          {/* Schedule with inline Week / Month switch */}
          {isPM && (
            <div className="mx-2 mt-0.5 mb-0.5 rounded-xl overflow-hidden bg-slate-800/40" data-testid="nav-schedule-switch">
              <div className="flex items-stretch">
                <Link to="/admin/schedule" data-testid="nav-schedule-week"
                  onClick={() => setDrawerOpen(false)}
                  className={`flex-1 flex items-center gap-3 px-3 py-2 font-bold uppercase text-[13px] tracking-wide transition-colors ${
                    (loc.pathname === "/admin/schedule")
                      ? "bg-gradient-to-r from-[#FF6A2B]/20 to-[#B12FBB]/10 text-white"
                      : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
                  }`}>
                  {(loc.pathname === "/admin/schedule") && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-[#FF6A2B] to-[#B12FBB]"/>
                  )}
                  <Calendar weight="fill" size={20} className="text-cyan-600"/>
                  <span className="flex-1">Schedule</span>
                </Link>
                <div className="flex items-center gap-0.5 pr-2 py-1">
                  <Link to="/admin/schedule" data-testid="nav-schedule-w"
                    className={`h-6 px-2 rounded-md text-[10px] font-black uppercase tracking-widest flex items-center transition-colors ${
                      loc.pathname === "/admin/schedule"
                        ? "bg-white text-slate-900"
                        : "bg-slate-700/60 text-slate-300 hover:bg-slate-700"
                    }`}>W</Link>
                  <Link to="/admin/schedule/month" data-testid="nav-schedule-m"
                    className={`h-6 px-2 rounded-md text-[10px] font-black uppercase tracking-widest flex items-center transition-colors ${
                      loc.pathname === "/admin/schedule/month"
                        ? "bg-white text-slate-900"
                        : "bg-slate-700/60 text-slate-300 hover:bg-slate-700"
                    }`}>M</Link>
                </div>
              </div>
            </div>
          )}
          {isPM && navItem("/admin/lineup", <Truck weight="fill" size={20} className="text-cyan-500"/>, "Line-Up")}
          {isPM && navItem("/admin/lineup", <Broadcast weight="fill" size={20} className="text-cyan-400"/>, "Sweeper Radar")}
          {isPM && navItem("/admin/turns-board", <Repeat weight="fill" size={20} className="text-emerald-500"/>, "Turns Board")}
          {isPM && navItem("/admin/vacant-security", <ShieldCheck weight="fill" size={20} className="text-emerald-500"/>, "Vacant Security")}
          {isPM && navItem("/admin/inspections", <ClipboardText weight="fill" size={20} className="text-fuchsia-500"/>, "Inspections")}
        </NavGroup>

        {/* PORTFOLIO */}
        <NavGroup label="Portfolio" storageKey="nav-g-portfolio-v2" defaultOpen={true}>
          {navItem("/admin/properties", <Buildings weight="fill" size={20} className="text-slate-200"/>, "Properties")}
          {isPM && navItem("/admin/landlords", <Users weight="fill" size={20} className="text-amber-500"/>, "Landlords")}
          {isPM && navItem("/admin/tenant-bridge", <Users weight="fill" size={20} className="text-cyan-400"/>, "Tenants")}
          {navItem("/admin/vendors", <Wrench weight="fill" size={20} className="text-fuchsia-500"/>, "Vendors")}
          {navItem("/admin/portfolio-hub", <Buildings weight="regular" size={20} className="text-slate-300"/>, "Portfolios")}
        </NavGroup>

        {/* BILLING */}
        {(isPM || isFullAdmin) && (
          <NavGroup label="Billing" storageKey="nav-g-billing-v2" defaultOpen={false}>
            {navItem("/admin/pmr-billing", <Coins weight="fill" size={20} className="text-emerald-500"/>, "PMR Rebill")}
            {navItem("/admin/lcv-invoice-drop", <FileText weight="fill" size={20} className="text-amber-500"/>, "LCV Invoices")}
            {navItem("/admin/ready-to-bill", <CheckCircle weight="fill" size={20} className="text-emerald-500"/>, "Ready to Bill")}
            {navItem("/admin/receipts", <Storefront weight="fill" size={20} className="text-slate-300"/>, "Bookkeeper")}
          </NavGroup>
        )}

        {/* RADARS */}
        <NavGroup label="Radars" storageKey="nav-g-radars-v2" href="/admin/radars" defaultOpen={false}>
          {isPM && navItem("/admin/move-radar", <Repeat weight="fill" size={20} className="text-indigo-500"/>, "Move Radar")}
          {navItem("/admin/radars", <Scales weight="fill" size={20} className="text-slate-300"/>, "Legal Radar")}
          {isPM && navItem("/admin/appliance-iq", <Wrench weight="fill" size={20} className="text-amber-600"/>, "Appliance IQ")}
          {navItem("/admin/radars", <Broadcast weight="fill" size={20} className="text-red-500"/>, "Request Radar")}
          {navItem("/admin/tenant-qr", <QrCode weight="fill" size={20} className="text-cyan-400"/>, "Tenant QR Codes")}
        </NavGroup>

        {/* PEOPLE */}
        <NavGroup label="People" storageKey="nav-g-people-v2" defaultOpen={false}>
          {navItem("/admin/workers", <Users weight="fill" size={20} className="text-cyan-500"/>, "Workers")}
          {navItem("/admin/sub-contractors", <Users weight="fill" size={20} className="text-fuchsia-500"/>, "Sub-Contractors")}
          {navItem("/admin/clients", <Users weight="fill" size={20} className="text-amber-400"/>, "Customers")}
        </NavGroup>

        {/* INTEGRATIONS & AI */}
        <NavGroup label="Integrations & AI" storageKey="nav-g-integrations-v2" defaultOpen={false}>
          {navItem("/admin/clickup", <LinkSimple weight="bold" size={20} className="text-violet-500"/>, "ClickUp Sync")}
          {navItem("/admin/imports/appfolio", <Buildings weight="fill" size={20} className="text-emerald-600"/>, "AppFolio")}
          {navItem("/admin/imports/grok", <LinkSimple weight="fill" size={20} className="text-fuchsia-500"/>, "Grok Inspection")}
          {navItem("/admin/settings/inspection-templates", <ListChecks weight="fill" size={20} className="text-fuchsia-500"/>, "InspectRadar Templates")}
          {navItem("/admin/kraaft-import", <Camera weight="fill" size={20} className="text-cyan-500"/>, "Kraaft")}
          {navItem("/admin/whatsapp-import", <Phone weight="fill" size={20} className="text-emerald-500"/>, "Twilio SMS/MMS")}
          {navItem("/admin/integrations", <PuzzlePiece weight="fill" size={20} className="text-amber-500"/>, "All Integrations")}
          {navItem("/admin/ai-help", <MagicWand weight="fill" size={20} className="text-fuchsia-500"/>, "AI Tools")}
        </NavGroup>

        {/* ADMIN */}
        <NavGroup label="Admin" storageKey="nav-g-admin-v2" defaultOpen={false}>
          {isFullAdmin && navItem("/admin/reports", <ChartBar weight="bold" size={20} className="text-cyan-400"/>, "Reports")}
          {navItem("/admin/activity", <ClockCounterClockwise weight="bold" size={20} className="text-slate-300"/>, "Activity Log")}
          {isFullAdmin && navItem("/admin/cleanup-auto-wos", <Database weight="fill" size={20} className="text-sky-500"/>, "Cleanup")}
          {isFullAdmin && navItem("/admin/settings", <Gear weight="bold" size={20}/>, "Settings")}
        </NavGroup>

        {/* Major portfolios — still valuable, kept as small tail group */}
        {majorPortfolios.length > 0 && isPM && (
          <NavGroup label="Major Portfolios" storageKey="nav-g-major-v2" href="/admin/major-portfolios-hub" defaultOpen={false} count={majorPortfolios.length}>
            {majorPortfolios.map(p => navItem(`/admin/portfolios/${p.slug}`,
              <Buildings weight="fill" size={20} style={{ color: p.brand_color }}/>,
              p.name))}
          </NavGroup>
        )}

          </>
        )}
      </nav>
      <div className="mx-4 h-px bg-slate-800/60"/>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0">
            <div className="text-xs uppercase font-bold text-slate-500">Signed in</div>
            <div className="font-bold truncate">{user?.name}</div>
            <div className="mt-1 flex items-center gap-2 min-w-0" data-testid="sidebar-tenant-brand">
              {logoUrl && <span className="h-6 w-6 rounded bg-white flex items-center justify-center overflow-hidden shrink-0"><img src={logoUrl} alt="" className="max-h-5 max-w-5 object-contain"/></span>}
              <span className="text-[11px] font-black uppercase tracking-wider text-slate-300 truncate">{tenant?.name}</span>
            </div>
            <TierChip tenant={tenant}/>
          </div>
          <NotificationBell dark/>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn"
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-sm font-bold uppercase tracking-wide transition-colors">
          <SignOut weight="bold" size={16}/> Sign Out
        </button>
        <div className="mt-3 text-[9px] text-slate-500 text-center font-black uppercase tracking-widest" data-testid="powered-by-mark">
          Powered by Field Radar
        </div>
      </div>
    </>
  );
  const isSetupWizard = loc.pathname === "/admin/landlord/setup";
  return (
    <div className="min-h-screen flex flex-col bg-[#f4f4f5]">
      <KnownAddressDatalist/>
      <ImpersonationBanner/>
      <GlobalEscalationBar/>
      {!isSetupWizard && <TrialBanner/>}
      <div className="flex-1 md:flex min-w-0">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-[#0f172a] text-white flex items-center justify-between px-4 h-14">
        <button onClick={() => setDrawerOpen(true)} data-testid="admin-drawer-open"
          className="p-2 -ml-2">
          <List weight="bold" size={24}/>
        </button>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center overflow-hidden">
            {logoUrl ? <img src={logoUrl} alt="logo" className="max-h-7 max-w-7 object-contain"/> : <Logo size={36}/>}
          </div>
          <span className="font-display font-black text-base truncate max-w-[140px]">{tenant?.name || "Field Radar"}</span>
        </div>
        <NotificationBell dark/>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex shrink-0 md:my-3 md:ml-3 md:mr-2 md:rounded-2xl md:overflow-hidden w-64 bg-[#0f172a] text-white flex-col md:sticky md:top-3 md:h-[calc(100vh-1.5rem)] shadow-[0_10px_30px_rgba(15,23,42,0.15)]">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setDrawerOpen(false)}/>
          <aside data-testid="admin-drawer" className="md:hidden fixed left-0 top-0 bottom-0 z-50 w-72 bg-[#0f172a] text-white flex flex-col shadow-2xl rounded-r-2xl">
            {SidebarContent}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 [overflow-x:clip]">
        {/* Global search — sticky top strip on both desktop + mobile */}
        <div className="sticky top-0 md:top-3 z-50 bg-[#f4f4f5]/95 backdrop-blur border-b border-slate-200 px-3 md:px-5 py-2.5 md:py-3 flex items-center gap-3" data-testid="global-search-strip">
          <div className="max-w-3xl flex-1 min-w-0">
            <GlobalSearchBar/>
          </div>
          <DesignVersionPill/>
          {tenant?.name && (
            <div className="hidden md:inline-flex items-center gap-2 pl-2 pr-3 py-1 rounded-full bg-white border border-slate-200 shadow-sm shrink-0" data-testid="topbar-tenant-chip" title={tenant.name}>
              {logoUrl ? <img src={logoUrl} alt="" className="h-6 w-6 object-contain"/> : <span className="h-6 w-6 rounded-full bg-[#0B1C2D] text-white text-[10px] font-black flex items-center justify-center">{tenant.name.slice(0,2).toUpperCase()}</span>}
              <span className="text-[11px] font-black uppercase tracking-wider text-[#0B1C2D] max-w-[160px] truncate">{tenant.name}</span>
            </div>
          )}
        </div>
        {!isSetupWizard && killswitch?.enabled && (
          <div className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 flex items-center justify-between gap-3" data-testid="email-killswitch-banner">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <Warning weight="fill" size={14}/>
              </div>
              <span className="text-xs font-bold tracking-wide truncate">
                Test Mode — outbound emails blocked
                {killswitch.source === "env" && <span className="ml-2 font-mono opacity-70">(via .env)</span>}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {killswitch.source !== "env" && (
                <button data-testid="killswitch-turn-off-btn"
                  onClick={async () => {
                    if (!window.confirm("Turn OFF the killswitch? All outbound emails will start flowing (retention digests, quote reminders, landlord invites, AI update sends).")) return;
                    try {
                      await api.post("/system/email-killswitch", { enabled: false });
                      window.location.reload();
                    } catch (e) {
                      alert("Failed — you may not have super-admin permission");
                    }
                  }}
                  className="h-7 px-3 rounded-md bg-white/15 hover:bg-white/25 text-white font-bold text-[10px] uppercase tracking-widest transition-colors">
                  ⚡ Turn Off
                </button>
              )}
              <Link to="/admin/settings" data-testid="killswitch-banner-link"
                className="text-[10px] font-bold uppercase tracking-widest opacity-90 hover:opacity-100 hover:underline">
                Manage →
              </Link>
            </div>
          </div>
        )}
        {children}
        {/* Landlord-only disclaimer at bottom of main content — sits inside
            <main> so the flex-row shell doesn't collapse the dashboard. */}
        <LandlordDisclaimerFooter/>
      </main>
      </div>
      <AskProManBubble/>
      <QuickCaptureFab/>
      <PaywallModal/>
      {!isSetupWizard && <LandlordTosGate/>}
    </div>
  );
}

export function WorkerShell({ children }) {
  const { t } = useT();
  const { user, logout } = useAuth();
  const tenant = useTenant();
  const logoUrl = tenant?.logo_path ? `${API}/tenant/logo/${tenant.id}` : null;
  // Team-chat unread — badges the chat icon in the top-right so techs know
  // office wrote them without opening the app. Same 15s poll as admin sidebar.
  // Fires an audible chime + toast on rising count when NOT on the chat page.
  const [chatUnread, setChatUnread] = useState(0);
  const chatPrevRef = useRef(null);
  const loc = useLocation();
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = () => {
      api.get("/team-chat/unread-total")
        .then(r => {
          if (!alive) return;
          const next = r.data?.total || 0;
          const prev = chatPrevRef.current;
          setChatUnread(next);
          // Home-screen icon badge for workers too (PWA install required)
          try {
            if (typeof navigator !== "undefined" && "setAppBadge" in navigator) {
              if (next > 0) navigator.setAppBadge(next).catch(() => {});
              else navigator.clearAppBadge?.().catch(() => {});
            }
          } catch { /* silent */ }
          const onChatPage = loc.pathname.startsWith("/worker/team-chat") || loc.pathname.startsWith("/admin/team-chat");
          if (prev != null && next > prev && !onChatPage) {
            const delta = next - prev;
            toast.info(`💬 ${delta} new message${delta === 1 ? "" : "s"} from office`, {
              duration: 6000,
            });
            playChatChime();
          }
          chatPrevRef.current = next;
        })
        .catch(() => {});
    };
    tick();
    const timer = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loc.pathname]);
  return (
    <div className="min-h-screen bg-[#f4f4f5]">
      <KnownAddressDatalist/>
      <ImpersonationBanner/>
      <GlobalEscalationBar/>
      <header className="sticky top-0 z-40 bg-[#0f172a] text-white">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/worker" className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 bg-white flex items-center justify-center border-2 border-white overflow-hidden flex-shrink-0">
              {logoUrl ? <img src={logoUrl} alt="logo" className="max-h-9 max-w-9 object-contain"/> : <Logo size={40}/>}
            </div>
            <span className="hidden xs:inline font-display font-black text-lg truncate max-w-[120px]">{tenant?.name || "Field Radar"}</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <SimpleModeToggle/>
            <LangToggle/>
            <Link to="/worker/team-chat" data-testid="worker-team-chat-btn"
              title="Team Chat — message the office"
              className="relative h-10 w-10 bg-[#00C4CC] hover:bg-[#00a8af] flex items-center justify-center flex-shrink-0">
              <ChatCircleText weight="fill" size={20}/>
              {chatUnread > 0 && (
                <span data-testid="worker-team-chat-badge"
                      className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-[#0f172a] animate-pulse">
                  {chatUnread > 99 ? "99+" : chatUnread}
                </span>
              )}
            </Link>
            <NotificationBell dark/>
            <div className="text-right hidden sm:block">
              <div className="text-xs uppercase text-slate-400 font-bold">{t("worker.tech")}</div>
              <div className="text-sm font-bold truncate max-w-[100px]">{user?.name}</div>
              <TierChip tenant={tenant} compact/>
            </div>
            <button onClick={logout} data-testid="worker-logout-btn" className="h-10 w-10 bg-slate-800 hover:bg-slate-700 flex items-center justify-center flex-shrink-0">
              <SignOut weight="bold" size={18}/>
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-3xl mx-auto">{children}</div>
      <AskProManBubble/>
      <QuickCaptureFab/>
    </div>
  );
}
